import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { Form, FormikProvider } from 'formik';
import closeFill from '@iconify/icons-eva/close-fill';
import roundClearAll from '@iconify/icons-ic/round-clear-all';
import saveIcon from '@iconify/icons-eva/save-outline';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
// material
import {
  Box,
  Stack,
  Button,
  Drawer,
  Divider,
  IconButton,
  Typography,
  TextField,
  Autocomplete,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormGroup,
  Checkbox
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

//
import { useSelector } from 'react-redux';
import { useTranslation } from '../../common/LocalizationProvider';
import {
  selectDevicesArray,
  selectGroupSelectedId,
  selectGroupsArray,
  selectSession,
  selectUsersArray
} from '../../createSelectorUtils';

// ----------------------------------------------------------------------

ReportFilterSidebar.propTypes = {
  isOpenFilter: PropTypes.bool,
  onResetFilter: PropTypes.func,
  onOpenFilter: PropTypes.func,
  onCloseFilter: PropTypes.func,
  isSubmitting: PropTypes.func,
  formik: PropTypes.object,
  onSaveFilter: PropTypes.func,
  clubFilter: PropTypes.bool,
  deviceFilter: PropTypes.bool,
  userFilter: PropTypes.bool,
  tableHead: PropTypes.array,
  handleUpdateTableHead: PropTypes.func
};

export default function ReportFilterSidebar({
  isOpenFilter,
  onResetFilter,
  onOpenFilter,
  onCloseFilter,
  onSaveFilter,
  formik,
  clubFilter,
  deviceFilter,
  userFilter,
  tableHead,
  handleUpdateTableHead
}) {
  const t = useTranslation();
  const { getFieldProps, setFieldValue } = formik;
  const session = useSelector(selectSession);
  const groupId = useSelector(selectGroupSelectedId);
  const permission = session.permission
    ? session.permission.find((item) => item.groupid === groupId) || {}
    : {};
  const groups = useSelector(selectGroupsArray);

  const devicesArray = useSelector(selectDevicesArray);
  const usersArray = useSelector(selectUsersArray);

  const devices =
    formik.values.group == null
      ? devicesArray
      : devicesArray.filter((device) =>
          formik.values.group.id === 0
            ? device.groupId === 0 ||
              session.permission.find((item) => item.groupid === device.groupId) === undefined
            : device.groupId === formik.values.group.id
        );

  const users =
    formik.values.group == null
      ? usersArray
      : usersArray.filter(
          (user) =>
            user.permission &&
            user.permission.find((item) => item.groupid === formik.values.group.id)
        );

  return (
    <>
      <Button
        disableRipple
        color="inherit"
        endIcon={<Icon icon={roundFilterList} />}
        onClick={onOpenFilter}
      >
        {`${t('filters')} `}
      </Button>

      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate>
          <Drawer
            anchor="right"
            open={isOpenFilter}
            onClose={onCloseFilter}
            PaperProps={{
              sx: { width: 280, border: 'none', overflow: 'auto' }
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 1, py: 2 }}
            >
              <Typography variant="subtitle1" sx={{ ml: 1 }}>
                {t('filters')}
              </Typography>
              <IconButton onClick={onCloseFilter}>
                <Icon icon={closeFill} width={20} height={20} />
              </IconButton>
            </Stack>

            <Divider />

            <Stack spacing={3} sx={{ p: 2 }}>
              {getFieldProps('reportType').value !== undefined && (
                <Autocomplete
                  fullWidth
                  {...getFieldProps('reportType')}
                  options={['route', 'vols']}
                  getOptionLabel={(option) => option}
                  renderInput={(params) => (
                    <TextField {...params} label="Report" variant="standard" />
                  )}
                  onChange={(event, value) => setFieldValue('reportType', value)}
                  isOptionEqualToValue={(option, value) => option === value}
                />
              )}
              {clubFilter && session.administrator && (
                <Autocomplete
                  fullWidth
                  {...getFieldProps('group')}
                  options={groups}
                  getOptionLabel={(option) => (option.name === undefined ? '' : option.name)}
                  renderInput={(params) => (
                    <TextField {...params} label={t('fields_Club')} variant="standard" />
                  )}
                  onChange={(event, value) => setFieldValue('group', value)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              )}

              {deviceFilter && (
                <Autocomplete
                  fullWidth
                  {...getFieldProps('device')}
                  options={devices}
                  getOptionLabel={(option) =>
                    option.groupId === 0 ? `${option.name} - ${option.uniqueId}` : option.name || ''
                  }
                  renderInput={(params) => (
                    <TextField {...params} label={t('devicesList')} variant="standard" />
                  )}
                  onChange={(event, value) => setFieldValue('device', value)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              )}

              {userFilter &&
                (session.administrator || permission.status < 3) &&
                !(groupId !== 0 && session.administrator) && (
                  <Autocomplete
                    fullWidth
                    {...getFieldProps('user')}
                    options={users}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {`${option.prenom} ${option.name}`}
                      </li>
                    )}
                    getOptionLabel={(option) =>
                      option.name === undefined ? '' : `${option.prenom} ${option.name}`
                    }
                    renderInput={(params) => (
                      <TextField {...params} label={t('usersList')} variant="standard" />
                    )}
                    onChange={(event, value) => setFieldValue('user', value)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    disabled={!(getFieldProps('device').value || getFieldProps('group').value)}
                  />
                )}
            </Stack>
            <Stack spacing={2} sx={{ p: 2 }}>
              <FormControl variant="standard" fullWidth>
                <InputLabel>{t('filters_Period')}</InputLabel>
                <Select {...getFieldProps('period')}>
                  <MenuItem value="today">{t('filters_Period_Today')}</MenuItem>
                  <MenuItem value="yesterday">{t('filters_Period_Yesterday')}</MenuItem>
                  <MenuItem value="thisWeek">{t('filters_Period_ThisWeek')}</MenuItem>
                  <MenuItem value="previousWeek">{t('filters_Period_PreviousWeek')}</MenuItem>
                  <MenuItem value="thisMonth">{t('filters_Period_ThisMonth')}</MenuItem>
                  <MenuItem value="previousMonth">{t('filters_Period_PreviousMonth')}</MenuItem>
                  <MenuItem value="last3Month">{t('filters_Period_Last3Month')}</MenuItem>
                  <MenuItem value="last6Month">{t('filters_Period_Last6Month')}</MenuItem>
                  <MenuItem value="thisYear">{t('filters_Period_ThisYear')}</MenuItem>
                  <MenuItem value="custom">{t('filters_Period_Custom')}</MenuItem>
                </Select>
              </FormControl>

              {getFieldProps('period').value === 'custom' && (
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label={t('filters_Period_Custom_From')}
                    inputFormat="DD/MM/YYYY"
                    value={getFieldProps('from').value}
                    onChange={(e) => setFieldValue('from', e)}
                    renderInput={(params) => <TextField {...params} fullWidth variant="standard" />}
                  />

                  <DatePicker
                    label={t('filters_Period_Custom_To')}
                    inputFormat="DD/MM/YYYY"
                    value={getFieldProps('to').value}
                    onChange={(e) => setFieldValue('to', e)}
                    renderInput={(params) => <TextField {...params} fullWidth variant="standard" />}
                  />
                </LocalizationProvider>
              )}
            </Stack>

            <Box sx={{ p: 2 }}>
              <Button
                fullWidth
                size="large"
                type="submit"
                color="inherit"
                variant="outlined"
                onClick={onSaveFilter}
                startIcon={<Icon icon={saveIcon} />}
              >
                {t('btn_Apply')}
              </Button>
            </Box>

            {getFieldProps('favorite').value !== undefined && (
              <>
                <Divider />

                <Box sx={{ p: 2 }}>
                  <FormControl>
                    <FormControlLabel
                      control={<Checkbox />}
                      label={t('FL_Menu_Favorite')}
                      checked={getFieldProps('favorite').value}
                      {...getFieldProps('favorite')}
                      onChange={(event) => {
                        setFieldValue('favorite', event.target.checked);
                        window.localStorage.setItem(
                          'reportTripsFilterFavorite',
                          JSON.stringify(event.target.checked)
                        );
                      }}
                    />
                  </FormControl>
                </Box>
              </>
            )}
            {getFieldProps('assignFlight').value !== undefined && (
              <>
                <Divider />

                <Box sx={{ p: 2 }}>
                  <FormControl>
                    <RadioGroup
                      {...getFieldProps('assignFlight')}
                      onChange={(event) => {
                        setFieldValue('assignFlight', event.target.value);
                        window.localStorage.setItem(
                          'reportTripsFilterAssignFlight',
                          JSON.stringify(event.target.value)
                        );
                      }}
                    >
                      <FormControlLabel
                        value="unassigned"
                        control={<Radio />}
                        label={t('filters_AssignFlight_unassigned')}
                      />
                      <FormControlLabel
                        value="assigned"
                        control={<Radio />}
                        label={t('filters_AssignFlight_assigned')}
                      />
                      <FormControlLabel
                        value="all"
                        control={<Radio />}
                        label={t('filters_AssignFlight_all')}
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </>
            )}

            {getFieldProps('flag').value !== undefined && (
              <>
                <Divider />

                <Box sx={{ p: 2 }}>
                  <FormControl>
                    <RadioGroup
                      {...getFieldProps('flag')}
                      onChange={(event) => {
                        setFieldValue('flag', event.target.value);
                        window.localStorage.setItem(
                          'reportTripsFilterFlag',
                          JSON.stringify(event.target.value)
                        );
                      }}
                    >
                      <FormControlLabel
                        value="concour"
                        control={<Radio />}
                        label={t('FL_Flags_Competition')}
                      />
                      <FormControlLabel
                        value="revoir"
                        control={<Radio />}
                        label={t('FL_Flags_Review')}
                      />
                      <FormControlLabel
                        value="resolu"
                        control={<Radio />}
                        label={t('FL_Flags_Solve')}
                      />
                      <FormControlLabel
                        value="etude"
                        control={<Radio />}
                        label={t('FL_Flags_Progress')}
                      />

                      <FormControlLabel
                        value="signaler"
                        control={<Radio />}
                        label={t('FL_Flags_Anomaly')}
                      />
                      <FormControlLabel
                        value="all"
                        control={<Radio />}
                        label={t('filters_AssignFlight_all')}
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </>
            )}

            {getFieldProps('errorAnalyseFlight').value !== undefined && session.administrator && (
              <>
                <Divider />

                <Box sx={{ p: 2 }}>
                  <FormControl>
                    <FormControlLabel
                      control={<Checkbox />}
                      label={t('filters_Analyse_Error')}
                      checked={getFieldProps('errorAnalyseFlight').value}
                      {...getFieldProps('errorAnalyseFlight')}
                      onChange={(event) => {
                        setFieldValue('errorAnalyseFlight', event.target.checked);
                        window.localStorage.setItem(
                          'reportTripsFilterErrorAnalyseFlight',
                          JSON.stringify(event.target.checked)
                        );
                      }}
                    />
                  </FormControl>
                </Box>
              </>
            )}

            {getFieldProps('displayTaxiing').value !== undefined && (
              <>
                <Divider />

                <Box sx={{ p: 2 }}>
                  <FormControl>
                    <FormControlLabel
                      control={<Checkbox />}
                      label={t('tableTaxiing_Display')}
                      checked={getFieldProps('displayTaxiing').value}
                      {...getFieldProps('displayTaxiing')}
                      onChange={(event) => {
                        setFieldValue('displayTaxiing', event.target.checked);
                        window.localStorage.setItem(
                          'reportTripsDisplayTaxiing',
                          JSON.stringify(event.target.checked)
                        );
                      }}
                    />
                  </FormControl>
                </Box>
              </>
            )}

            {tableHead !== undefined && (
              <>
                <Divider />
                <Box sx={{ p: 2 }}>
                  <FormGroup>
                    {tableHead.map(
                      (head, index) =>
                        head.label !== undefined && (
                          <FormControlLabel
                            control={<Checkbox />}
                            label={t(head.label)}
                            key={index}
                            checked={head.display === undefined}
                            onChange={(event) => {
                              const newTableHead = tableHead.map((item) =>
                                item.id === head.id
                                  ? { ...head, display: event.target.checked ? undefined : 'none' }
                                  : item
                              );
                              handleUpdateTableHead(newTableHead);
                            }}
                          />
                        )
                    )}
                  </FormGroup>
                </Box>
                <Divider />
              </>
            )}

            <Box sx={{ p: 2 }}>
              <Button
                fullWidth
                size="large"
                type="submit"
                color="inherit"
                variant="outlined"
                onClick={onResetFilter}
                startIcon={<Icon icon={roundClearAll} />}
              >
                {t('btn_Clear')}
              </Button>
            </Box>
          </Drawer>
        </Form>
      </FormikProvider>
    </>
  );
}
